"use client";

import { Button } from "@/components/rac/button";
import { Tooltip, TooltipTrigger } from "@/components/rac/tooltip";
import { motion } from "framer-motion";
import { useRef } from "react";
import { ChatIcon, CloseIcon } from "./icons";

interface FABTriggerProps {
	isOpen: boolean;
	className?: string;
}

/**
 * Floating Action Button trigger for the chatbot
 * Draggable and shows different icons based on chat state
 */
export function FABTrigger({ isOpen, className = "" }: FABTriggerProps) {
	const isDraggingRef = useRef(false);
	const dragStartPosRef = useRef({ x: 0, y: 0 });

	const handleClick = (e: React.MouseEvent) => {
		if (isDraggingRef.current) {
			e.stopPropagation();
			e.preventDefault();
			return;
		}
		// toggle(); // Let Botpress handle the toggle via ID
	};

	return (
		<motion.div
			drag
			dragMomentum={false}
			onPointerDown={e => {
				dragStartPosRef.current = { x: e.clientX, y: e.clientY };
			}}
			onPointerUp={e => {
				const distance = Math.hypot(e.clientX - dragStartPosRef.current.x, e.clientY - dragStartPosRef.current.y);
				if (distance > 5) {
					isDraggingRef.current = true;
					setTimeout(() => {
						isDraggingRef.current = false;
					}, 200);
				}
			}}
			whileDrag={{ cursor: "grabbing", scale: 1.05 }}
			whileHover={{ scale: 1.05 }}
			className={`fixed bottom-6 right-6 z-[9998] ${className}`}
		>
			<TooltipTrigger>
				<button
					onClick={handleClick}
					id="bp-toggle-chat"
					className="w-14 h-14 rounded-full bg-[#E0454B] text-white shadow-lg hover:bg-[#c93e43] transition-all duration-300 flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-[#E0454B]/30"
					aria-label={isOpen ? "Close Chat" : "Open Chat"}
				>
					{isOpen ? <CloseIcon className="w-6 h-6" /> : <ChatIcon className="w-8 h-8" />}
				</button>
				<Tooltip>{isOpen ? "Close Chat" : "Chat with us"}</Tooltip>
			</TooltipTrigger>
		</motion.div>
	);
}

interface CTAButtonTriggerProps {}

/**
 * Small circular button for CTA bars
 */
export function CTAButtonTrigger({}: CTAButtonTriggerProps) {
	return (
		<TooltipTrigger>
			<button
				type="button"
				className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center justify-center cursor-pointer outline-none focus:ring-2 focus:ring-primary-500"
				aria-label="Chat with us"
				id="bp-toggle-chat"
			>
				<ChatIcon className="w-5 h-5" stroke="currentColor" />
			</button>
			<Tooltip>Chat with us</Tooltip>
		</TooltipTrigger>
	);
}

/**
 * Menu item trigger for dropdown menus
 */
export function MoreMenuTrigger() {
	return (
		<TooltipTrigger>
			<button
				type="button"
				className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center"
				id="bp-toggle-chat"
			>
				<ChatIcon className="w-4 h-4 mr-3" stroke="currentColor" />
				Chat with us
			</button>
			<Tooltip>Open chat</Tooltip>
		</TooltipTrigger>
	);
}

interface BigButtonTriggerProps {
	className?: string;
}

/**
 * Large prominent button for contact page
 */
export function BigButtonTrigger({ className = "" }: BigButtonTriggerProps) {
	return (
		<div className={className}>
			<TooltipTrigger>
				<Button
					variant="bareshell"
					className="w-full bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 text-white px-8 py-6 rounded-2xl font-semibold text-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-4 group"
					id="bp-toggle-chat"
				>
					<ChatIcon className="w-8 h-8 group-hover:animate-pulse" stroke="currentColor" />
					<span>Start Live Chat</span>
					<svg
						className="w-6 h-6 group-hover:translate-x-1 transition-transform"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
					</svg>
				</Button>
				<Tooltip>Chat with our AI assistant Sia</Tooltip>
			</TooltipTrigger>
		</div>
	);
}
